/*
  Расписание раскрытия веера (docs/brand-concept.md §4). Неспешный темп — сцена входа: каждый
  следующий лепесток стартует позже и садится дольше, последние два — с самым сильным замедлением.
  Быстрый темп — знак в меню. По этому же расписанию проявляются буквы слова Kaname: шесть букв —
  шесть лепестков.
*/

export interface BloomSchedule {
  /** Задержка старта каждого лепестка, мс (лепесток 0 — на 6 часах, дальше по часовой). */
  delays: readonly number[];
  /** Сколько лепесток выходит и садится, мс. */
  durations: readonly number[];
}

export const BLOOM_SLOW: BloomSchedule = {
  delays: [0, 400, 850, 1350, 1950, 2650],
  durations: [1100, 1150, 1250, 1400, 1700, 2000],
};

export const BLOOM_QUICK: BloomSchedule = {
  delays: [0, 150, 300, 450, 600, 750],
  durations: [640, 640, 640, 640, 640, 640],
};

/*
  Знак-загрузчик (R42): один лепесток — один пройденный шаг. Шаг — новый лепесток выходит за ms без
  расписания раскрытия; если за раз открылось несколько, они идут лесенкой stagger по порядку.
  Досадка на «готово» — то же, но быстро.
*/
export const STEP_REVEAL = { ms: 640, stagger: 150 } as const;
export const SNAP = { ms: 160, stagger: 30 } as const;

/** Тревога ядра: два удара и кольцо (KanameMark cue 'alarm'); повторы чаще ALARM_GAP_MS схлопываются. */
export const ALARM_MS = 900;
export const ALARM_GAP_MS = 1500;
/** Кольцо отклика вокруг ядра (совпадает с --animate-observer-burst в motion.css). */
export const RING_MS = 700;

/** Кривая посадки: обычная — мягкое замедление; у последних двух — заметно сильнее. */
export const EASE_SETTLE = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const EASE_SETTLE_STRONG = 'cubic-bezier(0.1, 0.9, 0.12, 1)';

/** Сколько лепестки темнеют в окна глубины, мс (совпадает с petal-depth в utilities.css). */
export const DEPTH_FADE_MS = 3000;

/** Когда садится последний лепесток. */
export function bloomEnd(s: BloomSchedule): number {
  return Math.max(...s.delays.map((d, i) => d + s.durations[i]));
}

/*
  Веер сервисов «Мои сервисы» (ServiceFan, spec-4 §5, §7). Наведение: пластина становится активной через
  ENTER из покоя, переход на другую — после DWELL на ней (быстрый пролёт не переключает); коридор к
  карточке держится не дольше AIM; ушёл с пластин — активная держится GRACE. Раздвигание раскрывается
  быстрее, чем сходится. Приход: остальные сходятся за COMPRESS, новая выходит через ARRIVE_DELAY и
  садится за ARRIVE. Свет наливается от заклёпки за LIGHT_ON, стекает за LIGHT_OFF. Уход пачкой — от
  крайней с шагом FOLD_STEP, остальные расходятся за SPREAD.
*/
export const FAN_HOVER_ENTER_MS = 60;
export const FAN_HOVER_DWELL_MS = 90;
export const FAN_AIM_MS = 300;
export const FAN_GRACE_MS = 400;
export const FAN_PART_OPEN_MS = 320;
export const FAN_PART_CLOSE_MS = 480;
export const FAN_COMPRESS_MS = 900;
export const FAN_ARRIVE_MS = 1100;
export const FAN_ARRIVE_DELAY_MS = 150;
export const FAN_LIGHT_ON_MS = 720;
export const FAN_LIGHT_OFF_MS = 380;
export const FAN_FOLD_STEP_MS = 70;
export const FAN_SPREAD_MS = 480;
/** Складывание пластины: поворот и прозрачность (как petal-motion[data-folding] в utilities.css). */
export const FAN_FOLD_MS = 380;
export const FAN_FOLD_FADE_MS = 300;
/** Раскрытие веера с семи пластин: прозрачность 0 → 1. */
const FAN_OPEN_FADE_MS = 240;

/*
  Кривые движка веера (useFanMotion) числами: rAF-движок не читает CSS. Совпадают с primitive.css
  (--ease-standard, --ease-out-expo) и с petal-motion[data-folding] в utilities.css — правишь здесь,
  правь и там.
*/
export type Bezier = readonly [number, number, number, number];
export const EASE_STANDARD: Bezier = [0.4, 0, 0.2, 1];
export const EASE_OUT_EXPO: Bezier = [0.16, 1, 0.3, 1];
export const EASE_FOLD: Bezier = [0.55, 0, 0.75, 0.2];

export interface FanTiming {
  delay: number;
  duration: number;
  /** Прозрачность 0 → 1: своя длительность, с той же задержкой. */
  fade: number;
  ease: Bezier | string;
}

/**
 * Раскрытие веера (первый показ страницы за сеанс), пластина k из n.
 * До шести — ровно BLOOM_QUICK знака: каждая выходит из-за предыдущей. С семи — «веер открывается
 * от направляющей»: все сложены на 6 часах и одним движением расходятся по часовой, дальние дольше.
 */
export function fanOpenTiming(n: number, k: number): FanTiming {
  if (n <= 6) {
    const duration = BLOOM_QUICK.durations[k];
    return { delay: BLOOM_QUICK.delays[k], duration, fade: Math.round(duration * 0.7), ease: EASE_SETTLE };
  }
  return { delay: 0, duration: Math.round(600 + (700 * k) / n), fade: FAN_OPEN_FADE_MS, ease: EASE_SETTLE };
}

/**
 * Приход пластины k в веер до шести (кадр знака, spec-1): неспешно, по BLOOM_SLOW; from — первая
 * пришедшая за раз, она стартует сразу, остальные — в такт расписания.
 */
export function arrivalTiming(k: number, from: number): FanTiming {
  const duration = BLOOM_SLOW.durations[k];
  return {
    delay: Math.max(0, BLOOM_SLOW.delays[k] - BLOOM_SLOW.delays[from]),
    duration,
    fade: Math.round(duration * 0.7),
    ease: k >= 4 ? EASE_SETTLE_STRONG : EASE_SETTLE,
  };
}
